import type { CommandPromptItem } from './command-prompts';
import { displayPresetAgentName } from '@/components/prompt-library/preset-agent-options';


export type PromptParentTabMode = 'executive' | 'management';

export type PromptBadgeTone = 'executive' | 'sales' | 'individual' | 'scheduled' | 'management' | 'marketing' | 'operations';

export type PromptCardMeta = {
    badgeLabel: string;
    badgeTone: PromptBadgeTone;
    subtitle: string;
    headline: string;
    recommendedAgent: string;
    integrations: string[];
};

const META: Record<string, PromptCardMeta> = {
    'weekly-executive-briefing': {
        badgeLabel: 'EXECUTIVE',
        badgeTone: 'executive',
        subtitle: 'Weekly review',
        headline: 'What should leadership know about pipeline, risk, and open loops this week?',
        recommendedAgent: 'Analyst — compiles email, CRM, and calendar signals into an executive-ready briefing.',
        integrations: ['Email', 'Calendar', 'CRM', 'Fireflies', 'Slack'],
    },
    'daily-ceo-briefing': {
        badgeLabel: 'EXECUTIVE',
        badgeTone: 'executive',
        subtitle: 'Morning briefing',
        headline: 'What do I need to know this morning before the day runs away?',
        recommendedAgent: 'Analyst — pulls overnight email, chat, and meetings into a sub‑3‑minute brief.',
        integrations: ['Email', 'Calendar', 'Teams', 'Fireflies', 'Slack'],
    },
    'gods-eye-view-dashboard': {
        badgeLabel: 'CLIENT SUCCESS',
        badgeTone: 'sales',
        subtitle: 'Portfolio health',
        headline: 'Where are accounts green, amber, or at risk across every touchpoint?',
        recommendedAgent: 'Analyst — builds client cards from CRM, email, and meeting intel.',
        integrations: ['Email', 'CRM', 'Fireflies', 'Slack', 'Calendar'],
    },
    'missed-opportunities-report': {
        badgeLabel: 'SALES',
        badgeTone: 'sales',
        subtitle: 'Pipeline hygiene',
        headline: 'What revenue or urgency did we miss in the inbox in the last 30–90 days?',
        recommendedAgent: 'Analyst — scans threads for stalled replies, complaints, and buying signals.',
        integrations: ['Email', 'Calendar', 'CRM'],
    },
    'who-needs-reply': {
        badgeLabel: 'SALES',
        badgeTone: 'sales',
        subtitle: 'Response queue',
        headline: 'Who is waiting on me right now, and what should I say back?',
        recommendedAgent: 'Analyst — ranks unanswered threads and drafts replies in your voice when DNA is loaded.',
        integrations: ['Email', 'Calendar'],
    },
    'pipeline-health-check': {
        badgeLabel: 'SALES',
        badgeTone: 'sales',
        subtitle: 'CRM intelligence',
        headline: 'Which deals are stuck, ghosting, or worth a push today?',
        recommendedAgent: 'Analyst — reads CRM stages, activity, and dates for velocity and next actions.',
        integrations: ['CRM', 'Email', 'Calendar'],
    },
    'shadow-notes-intelligence': {
        badgeLabel: 'MANAGEMENT',
        badgeTone: 'management',
        subtitle: 'Email subtext',
        headline: 'What are people not saying in threads — and where is risk hiding?',
        recommendedAgent: 'Analyst — maps tone shifts, avoided topics, and promise gaps across email.',
        integrations: ['Email', 'Fireflies'],
    },
    'meeting-shadow-intelligence': {
        badgeLabel: 'MANAGEMENT',
        badgeTone: 'management',
        subtitle: 'Meeting prep',
        headline: 'What didn’t get said in meetings, and what should we watch next?',
        recommendedAgent: 'Analyst — compares transcripts for patterns, hesitation, and cross‑meeting themes.',
        integrations: ['Fireflies', 'Email', 'Calendar'],
    },
    'communication-dna-extraction': {
        badgeLabel: 'MARKETING',
        badgeTone: 'marketing',
        subtitle: 'Voice & rules',
        headline: 'How do I sound in email — and what rules should an AI follow to draft like me?',
        recommendedAgent: 'Analyst — profiles sent mail and outputs a deployable communication DNA pack.',
        integrations: ['Email'],
    },
    'auto-draft-email-replies': {
        badgeLabel: 'OPERATIONS',
        badgeTone: 'operations',
        subtitle: 'Drafting',
        headline: 'Draft replies in my voice for everything that still needs an answer.',
        recommendedAgent: 'Analyst — creates inbox drafts from threads using your communication DNA.',
        integrations: ['Email', 'Calendar'],
    },
    'morning-briefing': {
        badgeLabel: 'EXECUTIVE',
        badgeTone: 'executive',
        subtitle: 'Morning briefing',
        headline: 'What do I need to know this morning?',
        recommendedAgent: 'Do agent — overnight summary, priorities, people waiting, and quick wins.',
        integrations: ['Email', 'Calendar', 'Slack'],
    },
    'inbox-triage': {
        badgeLabel: 'INDIVIDUAL',
        badgeTone: 'individual',
        subtitle: 'Inbox',
        headline: 'Triage my last 24 hours: what to reply to, defer, or archive?',
        recommendedAgent: 'Do agent — scannable actions with priority and one‑line context per thread.',
        integrations: ['Email'],
    },
    'meeting-prep': {
        badgeLabel: 'SALES',
        badgeTone: 'sales',
        subtitle: 'Meeting prep',
        headline: 'What context and talking points do I need for today’s meetings?',
        recommendedAgent: 'Do agent — attendees, recent threads, promises, and suggested questions.',
        integrations: ['Calendar', 'Email', 'Fireflies'],
    },
    'quick-wins': {
        badgeLabel: 'INDIVIDUAL',
        badgeTone: 'individual',
        subtitle: 'Focus',
        headline: 'What can I finish in under five minutes with the highest payoff?',
        recommendedAgent: 'Do agent — batches micro‑actions you can clear in one sitting.',
        integrations: ['Email', 'Calendar', 'Slack'],
    },
};

/** Imperative copy when the Do tab is active (actions to run, not questions to explore). */
const DO_MODE_CARD_OVERRIDES: Partial<
    Record<string, Pick<PromptCardMeta, 'headline' | 'subtitle' | 'recommendedAgent'>>
> = {
    'morning-briefing': {
        subtitle: 'Morning briefing',
        headline: 'Generate a concise morning brief: overnight signals, priorities, and who needs you.',
        recommendedAgent:
            'Pulls urgent items, top five focus areas, and people waiting on your reply.',
    },
    'inbox-triage': {
        subtitle: 'Inbox',
        headline: 'Triage my last 24 hours: label each thread reply, defer, or archive with priority.',
        recommendedAgent: 'One‑line summaries and suggested next steps for every open thread.',
    },
    'meeting-prep': {
        subtitle: 'Meetings',
        headline: 'Prepare me for every meeting today: context, promises, and suggested talking points.',
        recommendedAgent: 'Stitches calendar, email, and notes into a prep sheet per meeting.',
    },
    'quick-wins': {
        subtitle: 'Batch',
        headline: 'List high‑impact actions I can complete in under five minutes and order them by payoff.',
        recommendedAgent: 'Surfaces micro‑tasks you can clear in one focused batch.',
    },
};

export function getPromptCardMeta(item: CommandPromptItem, mode: PromptParentTabMode): PromptCardMeta {
    const m = META[item.id];
    const base: PromptCardMeta =
        m ??
        ({
            badgeLabel: item.parent_category ?? 'WORKFLOW',
            badgeTone:
                item.parent_category === 'SEE' || item.parent_category === 'Executive'
                    ? 'executive'
                    : item.parent_category === 'DO' || item.parent_category === 'Management'
                      ? 'operations'
                      : 'operations',
            subtitle: item.title,
            headline: item.description ?? item.title,
            recommendedAgent: item.agent_name
                ? `${displayPresetAgentName(item.agent_name)}${item.agent_description ? ' — ' + item.agent_description : ''}`
                : mode === 'management'
                    ? 'Action agent — runs this workflow and can apply changes where you approve.'
                    : 'Analyst — reads connected tools and returns a structured summary.',
            integrations: item.tags && item.tags.length > 0 ? item.tags : ['Email', 'Calendar'],
        } satisfies PromptCardMeta);

    if (mode === 'management') {
        const ov = DO_MODE_CARD_OVERRIDES[item.id];
        if (ov) return { ...base, ...ov };
    }

    return base;
}
